import React, { useState, useEffect } from "react";
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import QuoteButton from './Quote/QuoteButton';
import QuoteModal from './Quote/QuoteModal';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const socialLinks = [
    { 
      href: "https://www.facebook.com/profile.php?id=61562074690775",
      icon: "/images/facebook.png",
      label: "Facebook"
    },
    {
      href: "https://www.instagram.com/a1movingsolutions/",
      icon: "/images/instagram1.png",
      label: "Instagram"
    },
    {
      href: "https://www.tiktok.com/@a1movingsolutions",
      icon: "/images/tiktok.png",
      label: "TikTok"
    }
  ];

// Scroll shrink effect
  useEffect(() => {
  const shrinkAt = 122;   // scrollY needed to shrink
  const growAt = 55;      // scrollY needed to grow back

  const handleScroll = () => {
    const y = window.scrollY;
    setIsScrolled(prev => {
      if (!prev && y > shrinkAt) return true;
      if (prev && y < growAt) return false;
      return prev;
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  return () => window.removeEventListener('scroll', handleScroll);
}, []);

// Navigation helper functions
  const handleSectionClick = (e, sectionId) => {
  e.preventDefault();
  setIsMobileMenuOpen(false);

  if (location.pathname === '/') {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });

  } else {
    navigate('/', { state: { scrollTo: sectionId } });
  }
  };

  const handleLogoClick = (e) => {
      e.preventDefault();
      setIsMobileMenuOpen(false);
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
      }
  };

  const linkBase =
    "text-red-600 relative after:content-[''] after:absolute after:top-7 after:left-0 " +
    "after:w-full after:h-1 after:bg-red-600 after:scale-x-0 after:origin-left " +
    "after:transition-transform after:duration-300 hover:after:scale-x-100 " +
    "hover:text-red-800 transition-transform duration-300";
 
  const navLinks = [
    { label: 'Services',   id: 'services'  },
    { label: 'Reviews',    id: 'reviews'   },
    { label: 'About Us',   id: 'about-us'  },
    { label: 'Contact Us', id: 'contact'   },
  ];

  // Render export
  return (
    <header className={`sticky top-0 z-50 bg-white shadow transition-all duration-300 ${
      isScrolled ? 'py-2' : 'py-4'
    }`}>
      <nav className="container mx-auto px-4">
        {/* Desktop Navigation */}
        <div className="hidden lg:flex justify-between items-center">

          {/* Logo */}
          <a href="/" onClick={handleLogoClick}>
            <img
              src="/images/logo.jpg"
              alt="A1 Moving Solutions"
              className={`transition-all duration-300 ${isScrolled ? 'h-16' : 'h-28 md:h-35'}`}
            />
          </a>
          {/* Navigation Links */}
          <ul className={`flex items-center space-x-9 text-red-600 font-semibold transition-all duration-300 ${
            isScrolled ? 'text-xl' : 'text-2xl'
          }`}>
            {navLinks.map(({label, id }) => (
              <li key={id}>
                <a href={`#${id}`} onClick={(e) => handleSectionClick(e, id)} className={linkBase}>
                  {label}
                </a>
              </li>
            ))}
            <li>
              <Link to="/pricing" className={linkBase} onClick={() => setIsMobileMenuOpen(false)}>
                Pricing
              </Link>
            </li>
            <li>
              <QuoteButton onClick={() => setIsQuoteModalOpen(true)} />
            </li>
          </ul>
            {/* Social Icons */}
          <div className="flex space-x-4">
            {socialLinks.map((social) => (
              <a
                key={social.href}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="transform hover:-translate-y-1 transition-transform duration-300"
                aria-label={social.label}
              >
                <img
                  src={social.icon}
                  alt={social.label}
                  className={`
                    ${isScrolled ? 'h-7 w-7' : 'h-8 w-8 md:h-10 md:w-10'}
                    rounded-full border-2 border-gray-200 
                    hover:border-red-600 transition-colors duration-300
                  `}
                />
              </a>
            ))}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden flex justify-between items-center">
          <a href="/" onClick={handleLogoClick}>
            <img
              src="/images/logo.jpg"
              alt="A1 Moving Solutions"
              className={`transition-all duration-300 ${isScrolled ? 'h-12' : 'h-16'}`}
            />
          </a>
          
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'max-h-[28rem] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <ul className="py-4 space-y-2">
            {navLinks.map(({ label, id }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  onClick={(e) => handleSectionClick(e, id)}
                  className="block py-2 px-4 text-gray-800 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors duration-200"
                >
                  {label}
                </a>
              </li>
            ))}
            <li>
              <Link
                to="/pricing"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 px-4 text-gray-800 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors duration-200"
              >
                Pricing
              </Link>
            </li>
            <li className="px-4 pt-2">
              <QuoteButton onClick={() => { setIsQuoteModalOpen(true); setIsMobileMenuOpen(false); }} />
            </li>
            <li className="px-4 pt-4">
              <div className="flex justify-center space-x-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.href}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transform hover:-translate-y-1 transition-transform duration-300"
                    aria-label={social.label}
                  >
                    <img
                      src={social.icon}
                      alt={social.label}
                      className="h-8 w-8 rounded-full border-2 border-gray-200 hover:border-red-600 transition-colors duration-300"
                    />
                  </a>
                ))}
              </div>
            </li>
          </ul>
        </div>
      </nav>
 
      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} />
    </header>
  );
};

export default Navigation;